/**
 * RevealStep — The "aha moment."
 *
 * Takes the onboarding data, runs a preview projection,
 * and reveals the user's projected wealth with a count-up animation.
 *
 * This is the emotional climax of onboarding — it needs to hit hard.
 */
import { useState, useEffect, useMemo } from "react";
import * as calc from '../../engine';
import { DEF } from '../../constants/defaults';
import { SWR_DEFAULT } from '../../constants/financial';
import { F } from '../../utils/format';
import { useApp } from '../../hooks/useApp';

/* ─── Animated count-up hook ─── */
function useCountUp(target, duration = 2000, delay = 400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const timeout = setTimeout(() => {
      const start = performance.now();
      const animate = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        // Ease-out cubic for a satisfying deceleration
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(target * eased));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, duration, delay]);
  return value;
}

export default function RevealStep({ data, c, onComplete }) {
  const { s } = useApp();
  const [revealed, setRevealed] = useState(false);

  /* Build a preview state by merging onboarding data into defaults */
  const previewState = useMemo(() => {
    const sv = data.savingsTarget;
    return {
      ...DEF,
      age: data.age,
      startAge: data.startAge,
      maxAge: data.maxAge,
      salary: data.salary,
      salGr: data.salGr,
      bonusR: data.bonusR,
      liv: data.liv,
      nw0: data.nw0,
      ret: data.ret,
      s22: sv,
      s25: Math.round(sv * 1.2),
      s30: Math.round(sv * 1.5),
      s35: Math.round(sv * 2),
    };
  }, [data]);

  /* Run the projection engine on the preview state */
  const proj = useMemo(() => calc.run(previewState), [previewState]);
  const pF = proj[proj.length - 1] || {};
  const fiAge = proj.find((d) => d.fi >= d.liv)?.a;
  const fiReached = fiAge != null;
  const swrIncome = pF.nw * SWR_DEFAULT;
  const growthMultiple = pF.nw / Math.max(1, data.nw0);

  /* Animate the big number */
  const displayNW = useCountUp(pF.nw, 2200, 500);

  /* Stagger the reveal of supporting metrics */
  useEffect(() => {
    const timer = setTimeout(() => setRevealed(true), 1800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ textAlign: "center", maxWidth: 520, width: "100%" }}>
      {/* Pre-headline */}
      <div style={{
        fontSize: 13, fontWeight: 600, color: c.blue,
        letterSpacing: "0.12em", textTransform: "uppercase",
        marginBottom: 16, opacity: 0,
        animation: "fadeSlideUp 0.6s ease 0.2s forwards",
      }}>
        Your Projected Wealth at {pF.a}
      </div>

      {/* The Big Number */}
      <div style={{
        fontSize: 64, fontWeight: 800, letterSpacing: "-0.05em",
        color: c.fg, lineHeight: 1, marginBottom: 8,
        fontVariantNumeric: "tabular-nums",
        opacity: 0,
        animation: "fadeSlideUp 0.8s cubic-bezier(0.25,0.1,0.25,1) 0.4s forwards",
        textShadow: s.dark ? `0 0 60px ${c.blue}25` : "none",
      }}>
        {F(displayNW)}
      </div>

      {/* Growth badge */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 16px", borderRadius: 20,
        background: s.dark ? "rgba(48,209,88,0.12)" : "rgba(48,209,88,0.08)",
        fontSize: 14, fontWeight: 600, color: c.green,
        opacity: 0,
        animation: "fadeSlideUp 0.6s ease 1.2s forwards",
      }}>
        {Math.round(growthMultiple)}× from where you are today
      </div>

      {/* Supporting metrics */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12,
        marginTop: 36,
        opacity: revealed ? 1 : 0,
        transform: revealed ? "translateY(0)" : "translateY(12px)",
        transition: "all 0.6s cubic-bezier(0.25,0.1,0.25,1)",
      }}>
        <MetricCard
          label="FI Age"
          value={fiReached ? `${fiAge}` : `>${data.maxAge}`}
          color={fiReached ? c.blue : c.orange}
          c={c} s={s}
        />
        <MetricCard
          label="Passive Income"
          value={`${F(swrIncome)}/yr`}
          color={c.green}
          c={c} s={s}
        />
        <MetricCard
          label="4% SWR Covers"
          value={`${((swrIncome) / data.liv).toFixed(1)}× expenses`}
          color={c.purple || c.blue}
          c={c} s={s}
        />
      </div>

      {/* Motivational tagline */}
      <p style={{
        fontSize: 15, color: c.muted, marginTop: 32, lineHeight: 1.6,
        opacity: revealed ? 1 : 0,
        transition: "opacity 0.8s ease 0.3s",
      }}>
        This is what consistency looks like.
        <br />
        <span style={{ color: c.fg, fontWeight: 600 }}>
          Your future self just got a plan.
        </span>
      </p>

      {/* CTA */}
      <button onClick={onComplete} style={{
        marginTop: 36, padding: "16px 48px", borderRadius: 14,
        border: "none", background: c.blue, color: "#fff",
        fontSize: 16, fontWeight: 700, cursor: "pointer",
        letterSpacing: "-0.01em",
        boxShadow: `0 4px 24px ${c.blue}40`,
        opacity: revealed ? 1 : 0,
        transform: revealed ? "translateY(0)" : "translateY(8px)",
        transition: "all 0.5s cubic-bezier(0.25,0.1,0.25,1) 0.4s",
      }}>
        Start Planning →
      </button>
    </div>
  );
}

/* ─── Small metric card for the reveal ─── */
function MetricCard({ label, value, color, c, s }) {
  return (
    <div style={{
      padding: "16px 12px", borderRadius: 14,
      background: s.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      border: `1px solid ${s.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: c.muted,
        letterSpacing: "0.08em", textTransform: "uppercase",
        marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 17, fontWeight: 700, color: color,
        letterSpacing: "-0.02em",
      }}>
        {value}
      </div>
    </div>
  );
}
